/**
 * Registry Bulk Upload — Studio ERP (Aug 2026). Clients/Vendors only.
 * Upload workflow: pick file -> preview (with per-duplicate Skip/Update
 * choice) -> commit -> success summary. CSV + XLSX supported.
 */
import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tag } from "@/components/Layout";
import { UploadCloud, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const LABEL = { clients: "Clients", vendors: "Vendors" };

export default function RegistryBulkUploadDialog({ tab, onClose, onDone }) {
  const [step, setStep] = useState("pick"); // pick | preview | done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { rows, summary, header_errors }
  const [headerErrors, setHeaderErrors] = useState([]);
  const [decisions, setDecisions] = useState({}); // { rowNum: "insert"|"update"|"skip" }
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const downloadTemplate = (format) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/registry/${tab}/bulk-template?format=${format}`;
    window.open(url, "_blank");
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setHeaderErrors([]); }
  };

  const runPreview = async () => {
    if (!file) { toast.error("Choose a CSV or Excel file first"); return; }
    setBusy(true);
    setHeaderErrors([]);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/registry/${tab}/bulk-preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.header_errors?.length) {
        setHeaderErrors(data.header_errors);
        return;
      }
      setPreview(data);
      const initial = {};
      data.rows.forEach((r) => { if (r.is_duplicate && !r.errors.length) initial[r.row] = "skip"; });
      setDecisions(initial);
      setStep("preview");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const runCommit = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("decisions", JSON.stringify(decisions));
      const { data } = await api.post(`/registry/${tab}/bulk-commit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data.summary);
      setStep("done");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const close = () => { onClose(); };

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="rounded-sm w-full sm:max-w-3xl max-h-[85vh] overflow-y-auto grid-cols-1" data-testid="registry-bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display inline-flex items-center gap-2"><UploadCloud size={16} className="text-[#F54800]" /> Bulk upload — {LABEL[tab]}</DialogTitle>
          <DialogDescription className="text-xs">Import many {LABEL[tab]?.toLowerCase()} at once from a CSV or Excel file.</DialogDescription>
        </DialogHeader>

        {step === "pick" && (
          <div className="space-y-4">
            {headerErrors.length > 0 && (
              <div className="bg-red-50 border border-red-300 text-red-800 text-xs rounded-sm px-3 py-2" data-testid="registry-bulk-header-errors">
                {headerErrors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => downloadTemplate("xlsx")} data-testid="registry-bulk-template-xlsx">
                <Download size={12} className="mr-1.5" /> Download template (.xlsx)
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => downloadTemplate("csv")} data-testid="registry-bulk-template-csv">
                <Download size={12} className="mr-1.5" /> Download template (.csv)
              </Button>
            </div>
            <div
              onClick={() => inputRef.current?.click()}
              className="border-2 border-dashed border-slate-200 hover:border-[#F54800] rounded-sm p-8 text-center cursor-pointer transition-colors"
              data-testid="registry-bulk-dropzone"
            >
              <FileSpreadsheet size={28} className="mx-auto text-slate-300 mb-2" />
              {file ? (
                <div className="text-sm font-medium text-slate-800">{file.name}</div>
              ) : (
                <div className="text-sm text-slate-500">Click to choose a .csv or .xlsx file</div>
              )}
              <input ref={inputRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={onPickFile} data-testid="registry-bulk-file-input" />
            </div>
          </div>
        )}

        {step === "preview" && preview && (
          <div className="space-y-3">
            <div className="flex gap-2 text-xs">
              <Tag tone="slate">{preview.summary.total} row{preview.summary.total === 1 ? "" : "s"}</Tag>
              <Tag tone="green">{preview.summary.new} new</Tag>
              <Tag tone="orange">{preview.summary.duplicates} duplicates</Tag>
              {preview.summary.invalid > 0 && <Tag tone="red">{preview.summary.invalid} invalid</Tag>}
            </div>
            <div className="max-h-[45vh] overflow-y-auto border border-slate-200 rounded-sm">
              <table className="w-full text-xs" data-testid="registry-bulk-preview-table">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    {["Row", "Name", "Company", "Email", "Status", "Action"].map((h) => (
                      <th key={h} className="label-overline px-2 py-2 text-left whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.rows.map((r) => (
                    <tr key={r.row} className={r.errors.length ? "bg-red-50" : r.is_duplicate ? "bg-yellow-50" : ""} data-testid={`registry-bulk-row-${r.row}`}>
                      <td className="px-2 py-2">{r.row}</td>
                      <td className="px-2 py-2">{r.data.name || r.raw?.name || "—"}</td>
                      <td className="px-2 py-2">{r.data.company || r.raw?.company || "—"}</td>
                      <td className="px-2 py-2">{r.data.email || r.raw?.email || "—"}</td>
                      <td className="px-2 py-2">
                        {r.errors.length ? (
                          <span className="text-red-700 inline-flex items-center gap-1" title={r.errors.join("; ")}><AlertTriangle size={11} /> {r.errors[0]}</span>
                        ) : r.is_duplicate ? (
                          <span className="text-yellow-700">matches existing ({r.duplicate_reason}) — {r.existing_name}</span>
                        ) : (
                          <span className="text-green-700">new</span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        {r.errors.length ? (
                          <span className="text-slate-400">skipped</span>
                        ) : r.is_duplicate ? (
                          <Select value={decisions[r.row] || "skip"} onValueChange={(v) => setDecisions({ ...decisions, [r.row]: v })}>
                            <SelectTrigger className="rounded-sm h-7 text-[11px] w-40" data-testid={`registry-bulk-action-${r.row}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="skip">Skip</SelectItem>
                              <SelectItem value="update">Update existing</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-slate-500">will create</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="text-center py-6" data-testid="registry-bulk-success-summary">
            <CheckCircle2 size={32} className="mx-auto text-green-600 mb-3" />
            <div className="font-display text-base font-bold text-slate-900">
              {result.created} New {LABEL[tab]} Added, {result.updated} Updated, {result.skipped} Skipped
              {result.failed > 0 && `, ${result.failed} Failed`}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "pick" && (
            <>
              <Button variant="outline" className="rounded-sm" onClick={close}>Cancel</Button>
              <Button className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={runPreview} disabled={!file || busy} data-testid="registry-bulk-preview-btn">
                {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null} Preview
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button variant="outline" className="rounded-sm" onClick={() => setStep("pick")}>Back</Button>
              <Button className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={runCommit} disabled={busy} data-testid="registry-bulk-commit-btn">
                {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null} Import
              </Button>
            </>
          )}
          {step === "done" && (
            <Button className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={close} data-testid="registry-bulk-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
